import type { PlatformCapability } from "@newform/constants";
import type { Platform } from "@newform-monorepo/db";
import {
  type Requirement,
  type RequirementKind,
  scope,
  TOKEN,
} from "./requirements";
import { GOOGLE_ADS_SCOPE } from "./scopes/google";
import { SNAPCHAT_MARKETING_SCOPE } from "./scopes/snapchat";

/**
 * Every feature that needs something from an ad-platform connection, and
 * exactly what it needs on each platform. This is the one place that says so:
 * launch gates, the Settings card, Will Bot's explanations and the OAuth
 * request all read it. See README.md in this folder for how to add one.
 */
export const ACCESS_CAPABILITIES = [
  "readMetrics",
  "syncAds",
  "launch",
  "preflight",
  "pauseAndPark",
  "manageAds",
  "uploadCreatives",
  "scaleCreate",
  "scaleGraduate",
  "audiences",
  "pixels",
  "metaPartnerLink",
  "metaPartnershipCode",
  "metaTagApproval",
  "metaCreatorMarketplace",
  "tiktokSpark",
  "tiktokCreativeRecs",
] as const;

export type AccessCapability = (typeof ACCESS_CAPABILITIES)[number];

export interface CapabilityRemediation {
  /** For NewForm staff: what to do and on whose account. */
  staff: string;
  /** For the client who owns the connection. */
  client: string;
  /** For a creator, when a creator-facing flow depends on this. */
  creator?: string;
}

export interface CapabilityDefinition {
  /** Sentence case, shown on the Settings card. */
  label: string;
  /**
   * The product surface in `@newform/constants` this capability belongs to.
   * The parity test holds the two together: a platform declares requirements
   * here exactly when that surface is supported there.
   */
  platformCapability: PlatformCapability | null;
  /** Requirements per platform. A platform that is absent does not offer it. */
  platforms: Partial<Record<Platform, readonly Requirement[]>>;
  remediation: CapabilityRemediation;
  /**
   * False when the feature degrades rather than fails without it (CTA
   * recommendations fall back to a default call to action), so a missing
   * requirement reads as "degraded", never "blocked".
   */
  blocking: boolean;
  /**
   * True for a capability almost no connection has or needs: it is left out
   * of every connection's baseline (the OAuth request, the Settings card,
   * Will Bot's explanations, the weekly health check and its alerts) and is
   * only requested or judged where a flow names it, through
   * `requestedScopesFor(..., [capability])` or `assertCapability`.
   */
  optIn?: boolean;
}

const META_READ = scope("ads_read", "ads_management");
const META_MANAGE = scope("ads_management");
const META_BUSINESS = scope("business_management");
const META_PAGES_LIST = scope("pages_show_list");
const META_PAGES_READ = scope("pages_read_engagement");
const META_IG_BASIC = scope("instagram_basic");
const META_BRANDED_CONTENT_ADS = scope("instagram_branded_content_ads_brand");
const META_BRANDED_CONTENT = scope("instagram_branded_content_brand");
const META_PAGES_METADATA = scope("pages_manage_metadata");
const META_PAGES_USER_CONTENT = scope("pages_read_user_content");
const META_MARKETPLACE_DISCOVERY = scope(
  "instagram_creator_marketplace_discovery",
);
const META_MARKETPLACE_MESSAGING = scope(
  "instagram_creator_marketplace_messaging",
);

const TIKTOK_REPORTING = scope("4");
const TIKTOK_READ_ADS = scope("220");
const TIKTOK_ADS = scope("2");
const TIKTOK_CREATIVE = scope("6");
const TIKTOK_PIXELS = scope("8");
const TIKTOK_READ_PIXELS = scope("800");
const TIKTOK_READ_AUDIENCES = scope("30");
const TIKTOK_POSTS = scope("69");
const TIKTOK_CTA_RECOMMENDATION = scope("6110100");

const GOOGLE_ADS = scope(GOOGLE_ADS_SCOPE);
const GOOGLE_MANAGER: Requirement = { kind: "googleManagerAccess" };
const SNAPCHAT = scope(SNAPCHAT_MARKETING_SCOPE);
const SNAPCHAT_ROLE: Requirement = { kind: "snapchatRole" };

const META_ROLE: Requirement = { kind: "adAccountRole", role: "ADVERTISE" };
const META_CAMPAIGN_WRITE: Requirement = { kind: "campaignWrite" };
const META_PAGE: Requirement = { kind: "metaPage", promotable: true };
const META_IG_IDENTITY: Requirement = {
  kind: "instagramIdentity",
  via: "either",
};

const META_LAUNCH: readonly Requirement[] = [
  TOKEN,
  META_MANAGE,
  META_BUSINESS,
  META_PAGES_LIST,
  META_PAGES_READ,
  META_ROLE,
  META_CAMPAIGN_WRITE,
  META_PAGE,
];
const TIKTOK_LAUNCH: readonly Requirement[] = [
  TOKEN,
  TIKTOK_ADS,
  TIKTOK_CREATIVE,
  TIKTOK_PIXELS,
  { kind: "tiktokIdentity" },
];
const GOOGLE_ALL: readonly Requirement[] = [TOKEN, GOOGLE_ADS, GOOGLE_MANAGER];
const SNAPCHAT_READ: readonly Requirement[] = [TOKEN, SNAPCHAT];
const SNAPCHAT_WRITE: readonly Requirement[] = [TOKEN, SNAPCHAT, SNAPCHAT_ROLE];

const RECONNECT_CLIENT =
  "Reconnect this account from Settings and approve every permission the platform asks for.";

const META_CAN_CREATE_ADS: Omit<CapabilityRemediation, "creator"> = {
  staff:
    "This is the connected Meta user's access, not a missing permission, so reconnecting as them won't help. Get them the Advertiser role or higher on this ad account in the client's Business settings, and check they aren't restricted from advertising. Then check the connection again, or reconnect as a user who can create ads.",
  client:
    "This is about the person connected, not a missing permission, so reconnecting as them won't help. They need to be able to create ads on this ad account in Meta Ads Manager: the Advertiser role or higher, and not restricted from advertising. Ask an admin in your Meta Business settings to fix that, then check the connection again. Or reconnect as someone who already can.",
};

/**
 * Fixes for requirements that are about who is connected rather than what
 * they approved, where "reconnect and approve every permission" sends people
 * round the OAuth loop for nothing. `evaluateCapability` uses these in place
 * of the capability's own remediation, but only when no permission or
 * credential is also missing: a reconnect may then be all that's wrong.
 * Kinds without an entry keep the capability's copy.
 */
export const REQUIREMENT_REMEDIATION: Partial<
  Record<RequirementKind, Omit<CapabilityRemediation, "creator">>
> = {
  adAccountRole: META_CAN_CREATE_ADS,
  campaignWrite: META_CAN_CREATE_ADS,
  metaPage: {
    staff:
      "The connected Meta user can't promote a Facebook Page. Give them access to the brand's Page in Business settings and check the connection again, or reconnect as someone who manages the Page.",
    client:
      "Give the person connected access to your Facebook Page in Meta Business settings, then check the connection again. Or reconnect as someone who manages the Page.",
  },
  snapchatRole: {
    staff:
      "The connected Snapchat user has no role that can manage ads on this ad account. Get them one and check the connection again, or reconnect as a user who has one.",
    client:
      "This is about the person connected, not a missing permission, so reconnecting as them won't help. Give them a role that can manage ads on this Snapchat ad account, then check the connection again. Or reconnect as someone who already has one.",
  },
  tiktokIdentity: {
    staff:
      "The TikTok ad account has no identity to run ads under. Add a custom identity or link a TikTok account in TikTok Ads Manager, then check the connection again.",
    client:
      "Add an identity in TikTok Ads Manager (a custom identity or a linked TikTok account), then check the connection again.",
  },
  googleManagerAccess: {
    staff:
      "Pick the Google Ads manager account in this account's settings. Framework reads the account through it.",
    client: "Pick the Google Ads manager account in this account's settings.",
  },
};

export const CAPABILITY_REQUIREMENTS: Record<
  AccessCapability,
  CapabilityDefinition
> = {
  readMetrics: {
    label: "Read metrics",
    platformCapability: "query",
    platforms: {
      META: [TOKEN, META_READ],
      TIKTOK: [TOKEN, TIKTOK_REPORTING],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_READ,
      APPLOVIN: [TOKEN],
    },
    remediation: {
      staff:
        "Reconnect the account with read access. Without it, reports and Will Bot have no spend or results for it.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  syncAds: {
    label: "Sync ads",
    platformCapability: "adSync",
    platforms: {
      META: [TOKEN, META_READ, META_PAGES_LIST, META_PAGES_READ],
      TIKTOK: [TOKEN, TIKTOK_REPORTING, TIKTOK_READ_ADS],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_READ,
    },
    remediation: {
      staff:
        "Reconnect the account with read access to ads and Pages so ad sync can list ads and their creatives.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  launch: {
    label: "Launch tests",
    platformCapability: "frameworkLaunch",
    platforms: {
      META: META_LAUNCH,
      TIKTOK: TIKTOK_LAUNCH,
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_WRITE,
    },
    remediation: {
      staff:
        "Reconnect with a user who can create campaigns on this ad account and who granted every manage permission.",
      client:
        "Reconnect this account as a user who can create campaigns in the ad platform, and approve every permission it asks for.",
    },
    blocking: true,
  },
  preflight: {
    label: "Check a launch before it runs",
    platformCapability: "frameworkLaunch",
    platforms: {
      META: [TOKEN, META_MANAGE, META_BUSINESS, META_ROLE],
      TIKTOK: [TOKEN, TIKTOK_ADS, TIKTOK_CREATIVE, TIKTOK_PIXELS],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_WRITE,
    },
    remediation: {
      staff:
        "Reconnect with manage permissions. The launch check asks the platform to validate the campaign, which needs the same access as a launch.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  pauseAndPark: {
    label: "Pause losing ads",
    platformCapability: "frameworkMonitoring",
    platforms: {
      META: [TOKEN, META_MANAGE, META_ROLE],
      TIKTOK: [TOKEN, TIKTOK_ADS],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_WRITE,
    },
    remediation: {
      staff:
        "Reconnect with manage permissions. Monitoring cannot pause losing ads without them, so they keep spending.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  manageAds: {
    label: "Create and edit campaigns, ad groups and ads",
    platformCapability: null,
    platforms: {
      META: [TOKEN, META_MANAGE, META_ROLE],
      TIKTOK: [TOKEN, TIKTOK_ADS],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_WRITE,
    },
    remediation: {
      staff:
        "Reconnect with ads management permissions. Every campaign, ad group and ad write is refused without them.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  uploadCreatives: {
    label: "Upload videos and images",
    platformCapability: null,
    platforms: {
      META: [TOKEN, META_MANAGE],
      TIKTOK: [TOKEN, TIKTOK_CREATIVE],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_WRITE,
    },
    remediation: {
      staff:
        "Reconnect with creative management permissions so videos and images can be uploaded to the ad account.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  scaleCreate: {
    label: "Create scale campaigns",
    platformCapability: "scaleCampaignCreation",
    platforms: {
      META: META_LAUNCH,
    },
    remediation: {
      staff:
        "Reconnect with a user who can create campaigns on this ad account before creating a scale campaign.",
      client:
        "Reconnect this account as a user who can create campaigns in Ads Manager.",
    },
    blocking: true,
  },
  scaleGraduate: {
    label: "Graduate winners to scale",
    platformCapability: "scaleGraduation",
    platforms: {
      META: META_LAUNCH,
      TIKTOK: [TOKEN, TIKTOK_ADS, TIKTOK_CREATIVE],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_WRITE,
    },
    remediation: {
      staff:
        "Reconnect with manage permissions so winners can be copied into the scale campaign.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  audiences: {
    label: "Use custom audiences",
    platformCapability: "frameworkLaunch",
    platforms: {
      META: [TOKEN, META_READ],
      TIKTOK: [TOKEN, TIKTOK_READ_AUDIENCES],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_READ,
    },
    remediation: {
      staff:
        "Reconnect with audience permissions. Without them the launch settings cannot list custom audiences.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  pixels: {
    label: "Use pixels",
    platformCapability: "frameworkLaunch",
    platforms: {
      META: [TOKEN, META_READ],
      TIKTOK: [TOKEN, TIKTOK_READ_PIXELS],
      GOOGLE: GOOGLE_ALL,
      SNAPCHAT: SNAPCHAT_READ,
    },
    remediation: {
      staff:
        "Reconnect with pixel permissions. Without them the launch settings cannot list pixels or conversion events.",
      client: RECONNECT_CLIENT,
    },
    blocking: true,
  },
  metaPartnerLink: {
    label: "Request partnership ads",
    platformCapability: null,
    platforms: {
      META: [TOKEN, META_IG_BASIC, META_BRANDED_CONTENT_ADS, META_IG_IDENTITY],
    },
    remediation: {
      staff:
        "Reconnect Meta as someone with a role on the NewForm Meta app and access to the brand's Page and Instagram. Meta drops the Instagram permissions for anyone else.",
      client:
        "Reconnect Meta as an admin of your Facebook Page and Instagram account, and approve the Instagram permissions.",
      creator:
        "Nothing to do on your side. The brand's Meta connection needs updating before it can send partnership requests.",
    },
    blocking: true,
  },
  metaPartnershipCode: {
    label: "Run ads from partnership codes",
    platformCapability: null,
    platforms: {
      META: [
        TOKEN,
        META_MANAGE,
        META_IG_BASIC,
        META_BRANDED_CONTENT_ADS,
        META_ROLE,
      ],
    },
    remediation: {
      staff:
        "Reconnect Meta as someone with a role on the NewForm Meta app, with manage and Instagram branded content permissions.",
      client:
        "Reconnect Meta and approve the Instagram branded content permissions.",
      creator:
        "Nothing to do on your side. The brand's Meta connection needs updating before it can use your code.",
    },
    blocking: true,
  },
  metaTagApproval: {
    label: "Manage tag approvals",
    platformCapability: null,
    platforms: {
      META: [TOKEN, META_BRANDED_CONTENT],
    },
    remediation: {
      staff:
        "Reconnect Meta with the instagram_branded_content_brand permission to manage who can tag the brand.",
      client:
        "Reconnect Meta and approve the Instagram branded content permission.",
    },
    blocking: false,
  },
  metaCreatorMarketplace: {
    label: "Invite creators from Creator Marketplace",
    platformCapability: null,
    // Meta's own dependency lists for the two Creator Marketplace permissions,
    // plus a Page-linked Instagram account: both APIs only take the token of
    // the Page the brand's Instagram is connected to.
    platforms: {
      META: [
        TOKEN,
        META_BUSINESS,
        META_PAGES_LIST,
        META_PAGES_METADATA,
        META_PAGES_USER_CONTENT,
        META_IG_BASIC,
        META_MARKETPLACE_DISCOVERY,
        META_MARKETPLACE_MESSAGING,
        { kind: "instagramIdentity", via: "page" },
      ],
    },
    remediation: {
      staff:
        "Connect Creator Marketplace from the program settings as someone with a role on the NewForm Meta app who administers the brand's Facebook Page. The brand's Instagram has to be linked to that Page and onboarded to Creator Marketplace.",
      client:
        "Reconnect Meta as an admin of your Facebook Page and approve the Creator Marketplace permissions.",
    },
    blocking: true,
    // Until Meta grants Advanced Access only users with a role on the app can
    // grant these, so as a baseline every client's connection would read as
    // broken (and be told so in Slack) over a feature they can't use.
    optIn: true,
  },
  tiktokSpark: {
    label: "Run Spark Ads",
    platformCapability: null,
    platforms: {
      TIKTOK: [TOKEN, TIKTOK_POSTS, { kind: "sparkAuth" }],
    },
    remediation: {
      staff:
        "Reconnect TikTok with TikTok Posts Management so authorized creator posts can run as Spark Ads.",
      client: "Reconnect TikTok and approve TikTok Posts Management.",
      creator:
        "Nothing to do on your side. The brand's TikTok connection needs updating before it can run your post.",
    },
    blocking: true,
  },
  tiktokCreativeRecs: {
    label: "Call to action recommendations",
    platformCapability: null,
    platforms: {
      TIKTOK: [TOKEN, TIKTOK_CTA_RECOMMENDATION],
    },
    remediation: {
      staff:
        "Reconnect TikTok with Creative Recommendation to get recommended calls to action. Launches still work with a default call to action.",
      client: "Reconnect TikTok and approve Creative Recommendation.",
    },
    blocking: false,
  },
};

export function requirementsFor(
  capability: AccessCapability,
  platform: Platform,
): readonly Requirement[] | null {
  return CAPABILITY_REQUIREMENTS[capability].platforms[platform] ?? null;
}

/**
 * The capabilities every connection on a platform is asked for and judged
 * on, in registry order. Opt-in capabilities are left out (see
 * `CapabilityDefinition.optIn`).
 */
export function capabilitiesForPlatform(
  platform: Platform,
): AccessCapability[] {
  return ACCESS_CAPABILITIES.filter(
    (capability) =>
      requirementsFor(capability, platform) !== null &&
      !CAPABILITY_REQUIREMENTS[capability].optIn,
  );
}

/**
 * What a manage connection must be able to do to count as working. The rest
 * (partnership ads, Spark, CTA recommendations, scale campaigns) belong to
 * features an org may not use, so they are reported but do not make a
 * connection read as broken.
 */
export const CORE_MANAGE_CAPABILITIES: readonly AccessCapability[] = [
  "readMetrics",
  "syncAds",
  "launch",
  "preflight",
  "pauseAndPark",
  "manageAds",
  "uploadCreatives",
  "audiences",
  "pixels",
];

/** Read-only connections promise these and nothing else. */
export const READ_CAPABILITIES: readonly AccessCapability[] = [
  "readMetrics",
  "syncAds",
];

/**
 * The scopes to ask for so a connection covers `capabilities`: the first
 * (canonical) id of every scope requirement, in registry order.
 */
export function oauthScopesFor(
  platform: Platform,
  capabilities: readonly AccessCapability[],
): string[] {
  const scopes: string[] = [];
  for (const capability of capabilities) {
    for (const requirement of requirementsFor(capability, platform) ?? []) {
      const primary =
        requirement.kind === "scope" ? requirement.anyOf[0] : undefined;
      if (primary && !scopes.includes(primary)) scopes.push(primary);
    }
  }
  return scopes;
}
